import React from 'react';
import './Badges.css';

/**
 * EmployeeIDBadge Component
 * Displays employee ID with icon
 */
export const EmployeeIDBadge = ({ employeeId, size = 'medium', showLabel = false, label = 'Employee ID:' }) => {
  return (
    <div className={`employee-id-badge-wrapper employee-id-badge-${size}`}>
      {showLabel && <span className="employee-id-label">{label}</span>}
      <span className="employee-id-badge">
        <span className="employee-id-icon">🆔</span>
        <span className="employee-id-value">{employeeId}</span>
      </span>
    </div>
  );
};

/**
 * DeviceIDBadge Component
 * Displays device ID with icon
 */
export const DeviceIDBadge = ({ deviceId, clickable = false, onClick = null, size = 'medium', title = undefined }) => {
  const handleClick = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (clickable && onClick && typeof onClick === 'function') {
      onClick(e);
    }
  };

  return (
    <span
      className={`device-id-badge device-id-badge-${size} ${clickable ? 'clickable' : ''}`}
      onClick={clickable ? handleClick : undefined}
      style={clickable ? { cursor: 'pointer' } : {}}
      title={title}
    >
      🆔 {deviceId || 'N/A'}
    </span>
  );
};

/**
 * ComplaintIDBadge Component
 * Displays complaint ID with icon
 */
export const ComplaintIDBadge = ({ complaintId, clickable = false, onClick = null, size = 'medium', title = undefined }) => {
  const handleClick = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (clickable && onClick && typeof onClick === 'function') {
      onClick(e);
    }
  };

  return (
    <span
      className={`complaint-id-badge complaint-id-badge-${size} ${clickable ? 'clickable' : ''}`}
      onClick={clickable ? handleClick : undefined}
      style={clickable ? { cursor: 'pointer' } : {}}
      title={title}
    >
      ⚠️ {complaintId || 'N/A'}
    </span>
  );
};

/**
 * DeviceCountBadge Component
 * Displays count of devices
 */
export const DeviceCountBadge = ({ count, size = 'medium' }) => {
  return (
    <span className={`device-count-badge device-count-badge-${size}`}>
      {count}
    </span>
  );
};

/**
 * ComplaintCountBadge Component
 * Displays count of complaints
 */
export const ComplaintCountBadge = ({ count, status = 'default', size = 'medium' }) => {
  return (
    <span className={`complaint-count-badge complaint-count-badge-${size} complaint-count-badge-${status}`}>
      {count}
    </span>
  );
};

/**
 * SeverityBadge — colored text only (no pill), so it stays distinct from StatusBadge.
 * Matches technician work-queue severity UX.
 */
export const getSeverityLabel = (severity) => {
  const normalized = String(severity || 'minor').toLowerCase();
  const labels = { critical: 'Critical', major: 'Major', minor: 'Minor' };
  return labels[normalized] || 'Minor';
};

export const SeverityBadge = ({ severity, size = 'medium' }) => {
  const normalized = String(severity || 'minor').toLowerCase();
  const safe = ['critical', 'major', 'minor'].includes(normalized) ? normalized : 'minor';

  return (
    <span className={`severity-badge severity-${safe} severity-badge-${size}`}>
      {getSeverityLabel(safe)}
    </span>
  );
};

/**
 * StatusBadge Component
 * Displays status with appropriate styling
 * Uses pill-style design for complaint statuses (open, in_progress, closed) and location statuses (active, inactive)
 * Supports variant='lg' for large badge style (used in location management pages)
 */
export const StatusBadge = ({ status, size = 'medium', variant = 'default' }) => {
  const statusConfig = {
    available: { label: 'Available', className: 'status-available', title: 'No open tickets' },
    'in-use': { label: 'In Use', className: 'status-in-use' },
    'in_use': { label: 'In Use', className: 'status-in-use' },
    maintenance: { label: 'Maintenance', className: 'status-maintenance' },
    retired: { label: 'Retired', className: 'status-retired' },
    working: { label: 'Working', className: 'status-working' },
    overloaded: { label: 'Overloaded', className: 'status-overloaded' },
    in_share: { label: 'Fair queue', className: 'status-in_share', title: 'Open tickets match assigned devices' },
    above_share: { label: 'Heavy queue', className: 'status-above_share', title: 'More open tickets than assigned devices justify' },
    below_share: { label: 'Light queue', className: 'status-below_share', title: 'Fewer open tickets than assigned devices justify' },
    no_coverage: { label: 'No devices', className: 'status-no_coverage', title: 'Not assigned as primary or secondary on any device' },
    open: { label: 'Open', className: 'status-open' },
    acknowledged: { label: 'Acknowledged', className: 'status-acknowledged' },
    in_progress: { label: 'In Progress', className: 'status-in_progress' },
    escalated_to_vendor: { label: 'Escalated to Vendor', className: 'status-escalated_to_vendor' },
    closed: { label: 'Closed', className: 'status-closed' },
    cancelled: { label: 'Cancelled', className: 'status-cancelled' },
    active: { label: 'Active', className: 'status-active' },
    inactive: { label: 'Inactive', className: 'status-inactive' }
  };

  const config = statusConfig[status] || { label: status, className: 'status-default' };
  
  // Determine badge class based on variant
  let badgeClass;
  const complaintPillStatuses = ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor', 'closed', 'cancelled', 'active', 'inactive', 'available', 'working', 'overloaded', 'in_share', 'above_share', 'below_share', 'no_coverage'];
  const usePill = variant === 'pill' || complaintPillStatuses.includes(status);

  if (variant === 'lg') {
    badgeClass = 'status-badge-lg';
  } else if (usePill) {
    badgeClass = size === 'small' ? 'status-pill status-pill-sm' : 'status-pill';
  } else {
    badgeClass = `status-badge status-badge-${size}`;
  }

  const pillLabel = config.label ? String(config.label).toUpperCase() : status;
  return (
    <span className={`${badgeClass} status-${status} ${config.className}`} title={config.title}>
      {variant === 'lg' ? (status === 'active' ? '✓ Active' : '⊗ Inactive') : (
        usePill ? pillLabel : config.label
      )}
    </span>
  );
};

/**
 * LocationCountBadge Component
 * Displays count of locations
 */
export const LocationCountBadge = ({ count, size = 'medium' }) => {
  return (
    <span className={`location-count-badge location-count-badge-${size}`}>
      {count}
    </span>
  );
};

/**
 * DepartmentBadge Component
 * Displays department name
 */
export const DepartmentBadge = ({ name, size = 'medium' }) => {
  return (
    <span className={`department-badge department-badge-${size}`}>
      {name || '-'}
    </span>
  );
};

/**
 * AssignmentBadge Component
 * Displays assignment type (primary/secondary) for technicians
 */
export const AssignmentBadge = ({ type = 'primary', size = 'medium' }) => {
  const config = {
    primary: {
      label: 'PRIMARY',
      icon: '👤',
      className: 'assignment-primary'
    },
    secondary: {
      label: 'SECONDARY',
      icon: '👥',
      className: 'assignment-secondary'
    }
  };

  const badgeConfig = config[type] || config.primary;

  return (
    <span className={`assignment-badge assignment-badge-${size} ${badgeConfig.className}`} title={`${badgeConfig.label} Technician`}>
      <span className="assignment-icon">{badgeConfig.icon}</span>
      <span className="assignment-label">{badgeConfig.label}</span>
    </span>
  );
};

/**
 * CriticalityBadge Component
 * Displays device-name criticality (critical, major, minor)
 */
const CRITICALITY_LABELS = {
  critical: 'Critical device',
  major: 'Major device',
  minor: 'Minor device',
};

const CRITICALITY_TOOLTIPS = {
  critical: 'Device type criticality: Critical — complaints typically open at this severity',
  major: 'Device type criticality: Major — complaints typically open at this severity',
  minor: 'Device type criticality: Minor — complaints typically open at this severity',
};

export const CriticalityBadge = ({ criticality, size = 'medium' }) => {
  if (!criticality) {
    return null;
  }

  const normalized = String(criticality).toLowerCase();
  const safe = ['critical', 'major', 'minor'].includes(normalized) ? normalized : 'minor';
  const label = CRITICALITY_LABELS[safe];

  return (
    <span
      className={`criticality-badge criticality-badge-${safe} criticality-badge-${size}`}
      title={CRITICALITY_TOOLTIPS[safe]}
      aria-label={CRITICALITY_TOOLTIPS[safe]}
    >
      {label}
    </span>
  );
};

export default {
  EmployeeIDBadge,
  DeviceIDBadge,
  ComplaintIDBadge,
  DeviceCountBadge,
  ComplaintCountBadge,
  StatusBadge,
  SeverityBadge,
  getSeverityLabel,
  LocationCountBadge,
  DepartmentBadge,
  AssignmentBadge,
  CriticalityBadge
};
